import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

type Row = {
  label: string;
  avd: string;
  rds: string;
  vpn: string;
};

const rows: Row[] = [
  {
    label: 'Modèle d’accès',
    avd: 'Session / Apps dans le cloud',
    rds: 'Session / Apps on-prem / VM',
    vpn: 'Accès au réseau complet LAN',
  },
  {
    label: 'Hébergement',
    avd: 'Azure (PaaS + VMs)',
    rds: 'Serveurs Windows Server',
    vpn: 'Équipements VPN + infra interne',
  },
  {
    label: 'Isolation réseau',
    avd: 'Forte: pas d’accès direct LAN depuis endpoint',
    rds: 'Moyenne: dépend du design segmenté',
    vpn: 'Faible: extension du LAN à l’endpoint',
  },
  {
    label: 'Données sur endpoint',
    avd: 'Aucune par défaut (affichage)',
    rds: 'Aucune par défaut (affichage)',
    vpn: 'Souvent locales (partages montés)',
  },
  {
    label: 'Posture sécurité',
    avd: 'Conditional Access, MFA, Intune, Defender',
    rds: 'GPO/NAC on-prem, MFA tiers',
    vpn: 'Hétérogène selon postes',
  },
  {
    label: 'Scalabilité',
    avd: 'Élastique, autoscale par pool',
    rds: 'Capacité fixe, scaling lent',
    vpn: 'Liée aux liens/systèmes VPN',
  },
  {
    label: 'Expérience',
    avd: 'Image standard, FSLogix, UX homogène',
    rds: 'Image serveur partagée, UX correcte',
    vpn: 'Dépend applis/latence, hétérogène',
  },
  {
    label: 'Coûts',
    avd: 'OPEX à l’usage, CAPEX faible',
    rds: 'CAPEX/maintenance élevés',
    vpn: 'CAPEX boîtiers + OPEX liens',
  },
  {
    label: 'Opérations',
    avd: 'Géré via Azure + IaC possible',
    rds: 'Patching/HA/RDS Gateway à gérer',
    vpn: 'Gestion endpoints + ACLs',
  },
  {
    label: 'L\'offre idéale pour',
    avd: 'Télétravail massif, apps legacy',
    rds: 'Parc interne déjà centralisé',
    vpn: 'Admin réseau, cas techniques',
  },
  {
    label: 'À retenir',
    avd: 'Accès session ou app cloisonné',
    rds: 'Session on-prem si déjà équipé',
    vpn: 'Accès réseau à limiter',
  },
];

export default function AVDComparison() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const defaultLabels = useMemo(
    () => [
      'Hébergement',
      'Isolation réseau',
      'Données sur endpoint',
      'Coûts',
      'L\'offre idéale pour',
      'À retenir',
    ],
    []
  );

  const { defaultRows, restRows } = useMemo(() => {
    const defaults = defaultLabels
      .map((label) => rows.find((r) => r.label === label))
      .filter(Boolean) as Row[];
    const rest = rows.filter((r) => !defaultLabels.includes(r.label));
    return { defaultRows: defaults, restRows: rest };
  }, [defaultLabels]);

  const displayedRows = expanded ? [...defaultRows, ...restRows] : defaultRows;

  return (
    <section aria-labelledby="avd-comparison-heading" className="tw-py-10">
      <div className="tw-container tw-mx-auto tw-px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="tw-relative tw-max-w-7xl tw-mx-auto"
        >
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-1 tw-pb-3">
            <h2 id="avd-comparison-heading" className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-text-gray-900 dark:tw-text-gray-100">
              {t('secureTeleworking.compare.title', 'Comparatif des solutions de télétravail')}
            </h2>
            <span className="tw-text-xs tw-font-medium tw-text-blue-700 dark:tw-text-blue-300 tw-bg-blue-50/70 dark:tw-bg-blue-900/30 tw-rounded-full tw-px-3 tw-py-1">
              {t('secureTeleworking.compare.badge', 'AVD mis en avant')}
            </span>
          </div>

          {/* Table, no card, no borders, no shadows */}
          <div className="tw-relative tw-overflow-x-auto" id="avd-comparison-table">
            <table className="tw-min-w-[720px] tw-w-full tw-text-left tw-align-middle">
              <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr>
                  <th scope="col" className="tw-p-3 tw-text-xs md:tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-neutral-900">
                    {t('secureTeleworking.compare.col.feature', 'Technologie')}
                  </th>
                  <th scope="col" className="tw-p-3 tw-text-xs md:tw-text-sm tw-font-semibold tw-text-blue-900 dark:tw-text-blue-100 tw-bg-blue-50 dark:tw-bg-blue-900/30">
                    {t('secureTeleworking.compare.col.avd', 'AVD (Azure Virtual Desktop)')}
                  </th>
                  <th scope="col" className="tw-p-3 tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-neutral-900">
                    {t('secureTeleworking.compare.col.rds', 'RDS / TSE (Remote Desktop Services)')}
                  </th>
                  <th scope="col" className="tw-p-3 tw-text-xs md:tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-neutral-900">
                    {t('secureTeleworking.compare.col.vpn', 'VPN')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((r) => (
                  <tr key={r.label} className="odd:tw-bg-neutral-50 dark:odd:tw-bg-neutral-900/30">
                    <th scope="row" className="tw-align-top tw-p-3 tw-text-[0.92rem] tw-font-medium tw-text-gray-900 dark:tw-text-gray-100">
                      {r.label}
                    </th>
                    <td className="tw-align-top tw-p-3 tw-text-gray-800 dark:tw-text-gray-200 tw-bg-blue-50/40 dark:tw-bg-blue-900/20">
                      {r.avd}
                    </td>
                    <td className="tw-align-top tw-p-3 tw-text-gray-800 dark:tw-text-gray-200">
                      {r.rds}
                    </td>
                    <td className="tw-align-top tw-p-3 tw-text-gray-800 dark:tw-text-gray-200">
                      {r.vpn}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-4 tw-flex tw-justify-start">
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls="avd-comparison-table"
              onClick={() => setExpanded((v) => !v)}
              className="tw-text-sm tw-font-medium tw-text-blue-700 dark:tw-text-blue-300 tw-bg-blue-50 hover:tw-bg-blue-100 dark:tw-bg-blue-900/30 dark:hover:tw-bg-blue-900/50 tw-rounded-full tw-px-4 tw-py-2 tw-transition-colors"
            >
              {expanded
                ? t('secureTeleworking.compare.showLess', 'Voir moins')
                : t('secureTeleworking.compare.showMore', 'Voir plus')}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
